import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, Subject } from 'rxjs';
import type { RetrieveCaseTypeOutput } from '@protocols/use-cases';

/**
 * One-shot result of an editing session. Payload-free: the draft the consumer
 * needs lives in this same shared state (`draft$`) and the staged ledgers
 * (`StagedCaseDataState`, `AssignedMethodTypesState`), so the event carries only
 * *which action* the user took. Mirrors `MethodTypeEditorOutcome`.
 */
export type CaseTypeEditorOutcome = { kind: 'saved' } | { kind: 'cancelled' };

/**
 * Persistable projection of the editing session's case-type row. The data
 * fields and the assigned method types live in their shared staging ledgers,
 * not in the draft.
 */
export interface CaseTypeDraft {
  id?: string;
  name: string;
  description: string;
}

interface CaseTypeEditorStateModel {
  id: string | null;
  name: string;
  description: string;
  /*
   * Whether the case type exists in the database. A create session carries no
   * id (`null`) with `persisted: false`; the save cascade reads the pair to
   * pick create-vs-update and flips it via `markPersisted` once the row save
   * returns the real id, so a retry takes the update path.
   */
  persisted: boolean;
  /*
   * Facade-owned i18n key for the blank-name save-validation error. Effects
   * never write here — the facade's save gate sets it and `setName` clears it
   * on non-blank input.
   */
  nameError: string | null;
  loading: boolean;
  /*
   * Raw exception from the most recent load failure. The facade dispatches it
   * to an i18n key in its own namespace.
   */
  loadException: Error | null;
  /** True while the cascade persists the case-type row (and its joins). */
  saving: boolean;
  /*
   * Raw exception from the most recent row/join save failure, recorded by the
   * save cascade (owner-state pattern, `MethodTypeEditorState.saveException`
   * parity). The facade dispatches it to an i18n key.
   */
  saveException: Error | null;
  /*
   * Signature snapshot captured when a seed completes (see
   * `CaseTypeEditorEffects.currentSignature$`). `null` until then, so a session that
   * never seeded (or whose load failed) reads as clean. Every seed updater
   * spreads INITIAL_STATE, clearing it per session; `markPersisted` spreads
   * the current state and preserves it.
   */
  baselineSignature: string | null;
}

const INITIAL_STATE: CaseTypeEditorStateModel = {
  id: null,
  name: '',
  description: '',
  persisted: false,
  nameError: null,
  loading: false,
  loadException: null,
  saving: false,
  saveException: null,
  baselineSignature: null,
};

/**
 * Per-page editing state for the case-type editor. Holds only the case-type ROW
 * draft (name + description) plus lifecycle flags and the one-shot save/cancel
 * outcome; the data fields and assigned method types live in their shared
 * staging ledgers. Trimmed mirror of `MethodTypeEditorState` (no asset
 * type / sample names / config). Provided once at page scope.
 */
@Injectable()
export class CaseTypeEditorState extends ComponentStore<CaseTypeEditorStateModel> {
  /**
   * One-shot save/cancel outcome. Event, not state — never replays to late
   * subscribers, so a reopened editor can't consume a prior session's result.
   */
  private readonly _outcome$ = new Subject<CaseTypeEditorOutcome>();
  public readonly outcome$: Observable<CaseTypeEditorOutcome> = this._outcome$.asObservable();

  constructor() {
    super({ ...INITIAL_STATE });
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public readonly id$ = this.select((s) => s.id);
  public readonly name$ = this.select((s) => s.name);
  public readonly description$ = this.select((s) => s.description);
  public readonly persisted$ = this.select((s) => s.persisted);
  public readonly nameError$ = this.select((s) => s.nameError);
  public readonly loading$ = this.select((s) => s.loading);
  public readonly loadException$ = this.select((s) => s.loadException);
  public readonly saving$ = this.select((s) => s.saving);
  public readonly saveException$ = this.select((s) => s.saveException);
  public readonly baselineSignature$ = this.select((s) => s.baselineSignature);

  public readonly draft$ = this.select(
    this.id$,
    this.name$,
    this.description$,
    (id, name, description): CaseTypeDraft => ({ id: id ?? undefined, name, description })
  );

  // ============================================================================
  // Updaters — metadata
  // ============================================================================

  /**
   * A non-blank name clears the pending validation error inline (the user fixed
   * it); a blank one leaves the error painted until the next save click
   * re-validates.
   */
  public readonly setName = this.updater((state, name: string) => ({
    ...state,
    name,
    nameError: name.trim() === '' ? state.nameError : null,
  }));

  public readonly setDescription = this.updater((state, description: string) => ({ ...state, description }));

  // ============================================================================
  // Updaters — flags
  // ============================================================================

  public readonly setNameError = this.updater((state, nameError: string | null) => ({ ...state, nameError }));
  public readonly setLoading = this.updater((state, loading: boolean) => ({ ...state, loading }));
  public readonly setSaving = this.updater((state, saving: boolean) => ({ ...state, saving }));
  // Plain slice setter (MethodTypeEditorState parity): the cascade flips
  // `saving` explicitly, so recording/clearing an exception never touches it.
  public readonly setSaveException = this.updater((state, saveException: Error | null) => ({
    ...state,
    saveException,
  }));

  // Setting an exception transitions out of the in-flight loading phase — that
  // is how "load failed" reaches the UI. Clearing (null) must NOT touch the
  // phase flag: the surrounding effect has just flipped it to true.
  public readonly setLoadException = this.updater((state, loadException: Error | null) => ({
    ...state,
    loadException,
    loading: loadException === null ? state.loading : false,
  }));

  // Seed updaters spread `...INITIAL_STATE`, so the baseline auto-clears on
  // every re-seed — the capture effect must re-run per session.
  public readonly setBaselineSignature = this.updater((state, baselineSignature: string) => ({
    ...state,
    baselineSignature,
  }));

  // ============================================================================
  // Updaters — lifecycle
  // ============================================================================

  public readonly reset = this.updater(() => ({ ...INITIAL_STATE }));

  /**
   * Seed a CREATE session: pristine editor with no id and `persisted: false`,
   * so the save cascade knows the row does not exist yet.
   */
  public readonly seedForCreate = this.updater(() => ({ ...INITIAL_STATE }));

  /**
   * The row save returned the REAL id: store it and mark the case type
   * persisted so any retry takes the update path.
   */
  public readonly markPersisted = this.updater((state, id: string) => ({ ...state, id, persisted: true }));

  /**
   * Seed the editor from a fresh load. Building on `INITIAL_STATE` also ends any
   * in-flight loading phase and wipes stale errors. The output's `fields` and
   * `methodTypes` are deliberately NOT stored here — the load effect seeds them
   * into the shared `StagedCaseDataState` / `AssignedMethodTypesState`.
   */
  public readonly seedFromCaseType = this.updater((_state, output: RetrieveCaseTypeOutput) => ({
    ...INITIAL_STATE,
    id: output.id,
    name: output.name,
    description: output.description,
    persisted: true,
  }));

  // ============================================================================
  // Events
  // ============================================================================

  /** Emit the editing-session outcome as a one-shot event (see `outcome$`). */
  public confirm(outcome: CaseTypeEditorOutcome): void {
    this._outcome$.next(outcome);
  }

  override ngOnDestroy(): void {
    this._outcome$.complete();
    super.ngOnDestroy();
  }
}
